import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  useGetMyProfile,
  useUpdateMyProfile,
  getGetMyProfileQueryKey,
} from '@/lib/api/orval/target/users/users';
import { useAuthStore } from '@/stores/authStore';
import { errorMessage } from '@/lib/errors';

export function MyProfile(): React.ReactElement {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasChecked = useAuthStore((s) => s.hasCheckedSession);
  const [readme, setReadme] = useState('');
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useGetMyProfile({
    query: { enabled: isAuthenticated },
  });

  useEffect(() => {
    if (data?.readme !== undefined) setReadme(data.readme);
  }, [data?.readme]);

  const update = useUpdateMyProfile({
    mutation: {
      onSuccess: () => {
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
        queryClient.invalidateQueries({ queryKey: getGetMyProfileQueryKey() });
      },
    },
  });

  if (hasChecked && !isAuthenticated) {
    return (
      <div className="themed-card p-6 text-center">
        <p className="mb-3">Log in to edit your profile.</p>
        <Link to="/login" className="btn-primary">
          Log in
        </Link>
      </div>
    );
  }

  if (isLoading) return <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>;

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <h1 className="text-2xl font-semibold">Your profile</h1>
      <Link to="/account/billing">Manage billing and credits</Link>
      {data?.user && (
        <p style={{ color: 'var(--text-secondary)' }}>
          @{data.user.username} ·{' '}
          <Link to={`/${data.user.username}`}>view public profile</Link>
        </p>
      )}
      <label className="text-sm font-medium">Bio (Markdown)</label>
      <textarea
        className="themed-input font-mono text-sm"
        rows={10}
        value={readme}
        onChange={(e) => setReadme(e.target.value)}
        placeholder="Tell people about yourself and your machines…"
      />
      <div className="flex items-center gap-3">
        <button
          className="btn-primary"
          onClick={() => update.mutate({ data: { readme } })}
          disabled={update.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
        {saved && (
          <span className="text-sm" style={{ color: 'var(--success)' }}>
            Saved
          </span>
        )}
        {update.isError && (
          <span className="text-sm" style={{ color: 'var(--danger)' }}>
            {errorMessage(update.error)}
          </span>
        )}
      </div>

      {data?.readme_html && (
        <>
          <h2 className="text-lg font-semibold mt-4">Preview</h2>
          <article
            className="themed-card p-6"
            dangerouslySetInnerHTML={{ __html: data.readme_html }}
          />
        </>
      )}
    </div>
  );
}
